import { useEffect, useMemo, useRef, useState } from "react";
import "../../styles/controls.css";
import "../../styles/layout.css";
import "../../styles/tokens.css";
import {
  ClassroomTools,
  RevealAnswersButton,
} from "../../components/ClassroomTools";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { ExhibitHeader } from "../../components/ExhibitHeader";
import { FullscreenToggle } from "../../components/FullscreenToggle";
import { StatusNotice } from "../../components/StatusNotice";
import {
  readStoredPreference,
  writeStoredPreference,
} from "../../hooks/localPrefs";
import type { SceneQuality } from "../../scene/SceneCanvas";
import { DemoCanvas } from "../shared/DemoCanvas";
import { DemoTour, type DemoTourStep } from "../shared/DemoTour";
import { readHashNumber, writeHashParams } from "../shared/hashState";
import { PLAYBACK_SPEEDS, usePlaybackClock } from "../shared/usePlaybackClock";
import { SeasonsFallback } from "./SeasonsFallback";
import { SeasonsScene } from "./SeasonsScene";
import {
  SEASON_SECONDS_AT_ONE_X,
  SEASON_STATIONS,
  YEAR_DAYS,
  getSeasonSnapshot,
} from "./geometry";

function localFallbackRequested() {
  return (
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("fallback") === "1"
  );
}

const PERIHELION_STORAGE_KEY = "pupil:seasons:perihelion";
const SPEED_STORAGE_KEY = "pupil:seasons:speed";

function resolveInitialDay(): number {
  const fromUrl = readHashNumber("day");
  return fromUrl !== null && fromUrl >= 0 && fromUrl <= YEAR_DAYS
    ? fromUrl
    : 172;
}

function formatHours(hours: number): string {
  if (hours >= 23.95) {
    return "24 h";
  }
  if (hours <= 0.05) {
    return "0 h";
  }
  return `${hours.toFixed(1)} h`;
}

export function SeasonsApp({
  forceFallback = false,
}: {
  readonly forceFallback?: boolean;
}) {
  const [fallbackActive, setFallbackActive] = useState(
    () => forceFallback || localFallbackRequested(),
  );
  const [sceneQuality, setSceneQuality] = useState<SceneQuality | null>(null);
  const [readoutVisible, setReadoutVisible] = useState(false);
  const [classroomMode, setClassroomMode] = useState(false);
  const [predictMode, setPredictMode] = useState(false);
  const [answersRevealed, setAnswersRevealed] = useState(false);
  const [showPerihelion, setShowPerihelion] = useState(
    () => readStoredPreference(PERIHELION_STORAGE_KEY) === "1",
  );
  const layoutRef = useRef<HTMLElement | null>(null);
  const clock = usePlaybackClock({
    cycleLength: YEAR_DAYS,
    initialValue: resolveInitialDay(),
    secondsPerCycleAtOneX: SEASON_SECONDS_AT_ONE_X,
    speedStorageKey: SPEED_STORAGE_KEY,
  });
  const snapshot = useMemo(() => getSeasonSnapshot(clock.value), [clock.value]);
  const togglePerihelion = () => {
    setShowPerihelion((current) => {
      writeStoredPreference(PERIHELION_STORAGE_KEY, current ? "0" : "1");
      return !current;
    });
  };
  const togglePredictMode = () => {
    setPredictMode((current) => !current);
    setAnswersRevealed(false);
  };

  // Keep the URL shareable whenever the year is paused or scrubbed. The
  // initial state above already reflects any incoming link, so the write can
  // never clobber it with defaults.
  useEffect(() => {
    if (clock.isPlaying) {
      return;
    }
    writeHashParams({ day: String(Math.round(clock.value)) });
  }, [clock.isPlaying, clock.value]);
  const pauseClock = () => {
    if (clock.isPlaying) {
      clock.togglePlayback();
    }
  };
  const tourSteps: readonly DemoTourStep[] = [
    {
      caption:
        "The Sun sits at a focus of Earth's orbit — not the centre. The orbit is drawn extra-stretched here so you can see that offset.",
      onEnter: pauseClock,
    },
    {
      caption:
        "June solstice: Earth's axis leans the North Pole toward the Sun — long days up north, short days down south.",
      onEnter: () => {
        pauseClock();
        clock.setValue(172);
      },
    },
    {
      caption:
        "December solstice: same tilt, other side of the Sun — now the South Pole leans in and the seasons flip. Tilt, not distance, makes the seasons.",
      onEnter: () => {
        pauseClock();
        clock.setValue(355);
      },
    },
  ];

  return (
    <main className="app-shell">
      <ExhibitHeader
        chain="Why Earth has Seasons → which hemisphere leans toward the Sun → day length and season"
        demoNumber="002"
        readoutVisible={readoutVisible}
        title="Earth's Seasons & Axial Tilt"
        onToggleReadout={() => setReadoutVisible((visible) => !visible)}
      />
      <section
        aria-label="Earth seasons simulation"
        className={`exhibit-layout${readoutVisible ? "" : " readout-hidden"}`}
        ref={layoutRef}
      >
        <section className="scene-region" aria-label="Yearly orbit view">
          <StatusNotice quality={fallbackActive ? null : sceneQuality} />
          <div className="scene-slot" data-testid="seasons-scene-slot">
            <FullscreenToggle targetRef={layoutRef} />
            <DemoTour steps={tourSteps} />
            {fallbackActive ? (
              <SeasonsFallback
                showPerihelion={showPerihelion}
                snapshot={snapshot}
              />
            ) : (
              <ErrorBoundary
                fallbackDescription="Continue with the same tilt-and-orbit geometry in the interactive 2D teaching view."
                onUseFallback={() => setFallbackActive(true)}
              >
                <DemoCanvas
                  ariaLabel="Interactive Earth orbit and axial tilt"
                  cameraPosition={[20, 16, 0]}
                  onFallbackNeeded={() => setFallbackActive(true)}
                  onQualityChange={setSceneQuality}
                >
                  <SeasonsScene
                    classroomMode={classroomMode}
                    isPlaying={clock.isPlaying}
                    showPerihelion={showPerihelion}
                    snapshot={snapshot}
                    speed={clock.speed}
                  />
                </DemoCanvas>
              </ErrorBoundary>
            )}
          </div>
          {forceFallback ? null : (
            <p className="fallback-switch">
              <button
                type="button"
                onClick={() => setFallbackActive((active) => !active)}
              >
                {fallbackActive
                  ? "Try the full 3D view"
                  : "Use the simple 2D view"}
              </button>
              {fallbackActive ? null : (
                <span> — for older devices or to save data.</span>
              )}
            </p>
          )}
          <p className="orientation-note">
            Earth's axis keeps the same tilt all year. The Sun sits at a focus
            of the ellipse, slightly toward January perihelion — not at the
            geometric centre. Seasons still come from tilt, not distance.
          </p>
        </section>
        {readoutVisible ? (
          <aside
            aria-label="Notes"
            className={`teaching-panel${
              predictMode && !answersRevealed ? " predict-mode" : ""
            }`}
          >
            <section className="information-panel">
              <h2>Notes</h2>
              {predictMode ? (
                <RevealAnswersButton
                  revealed={answersRevealed}
                  onToggle={() => setAnswersRevealed((current) => !current)}
                />
              ) : null}
              <p>
                <span aria-label="Current season">{snapshot.seasonName}</span>
              </p>
              <dl>
                <div>
                  <dt>Nearest station</dt>
                  <dd>{snapshot.station.label}</dd>
                </div>
                <div>
                  <dt>Solar declination</dt>
                  <dd>{snapshot.solarDeclinationDegrees.toFixed(1)}°</dd>
                </div>
                <div>
                  <dt>Day of year</dt>
                  <dd>{Math.round(snapshot.dayOfYear)}</dd>
                </div>
              </dl>
              <table className="reading-table">
                <caption>Noon Sun and day length</caption>
                <thead>
                  <tr>
                    <th scope="col">Place</th>
                    <th scope="col">Day length</th>
                    <th scope="col">Noon altitude</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshot.latitudes.map((band) => (
                    <tr key={band.label}>
                      <th scope="row">{band.label}</th>
                      <td>
                        {band.polarCondition === "midnight-sun"
                          ? "Midnight sun"
                          : band.polarCondition === "polar-night"
                            ? "Polar night"
                            : formatHours(band.dayLengthHours)}
                      </td>
                      <td>{Math.round(band.noonAltitudeDegrees)}°</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p>
                June: the North Pole leans toward the Sun. December: the South
                Pole does. Equinoxes split the day in half everywhere.
              </p>
            </section>
          </aside>
        ) : null}
        <section className="controls-panel" aria-label="Simulation controls">
          <fieldset className="control-group playback-controls">
            <legend>Playback</legend>
            <div className="button-row">
              <button
                aria-pressed={clock.isPlaying}
                type="button"
                onClick={clock.togglePlayback}
              >
                {clock.isPlaying ? "Pause year" : "Play year"}
              </button>
            </div>
            <label className="select-control" htmlFor="season-speed">
              Simulation speed
              <select
                id="season-speed"
                value={clock.speed}
                onChange={(event) =>
                  clock.setSpeed(
                    Number(
                      event.currentTarget.value,
                    ) as (typeof PLAYBACK_SPEEDS)[number],
                  )
                }
              >
                {PLAYBACK_SPEEDS.map((speed) => (
                  <option key={speed} value={speed}>
                    {speed}×
                  </option>
                ))}
              </select>
            </label>
          </fieldset>
          <section className="timeline" aria-label="Year timeline">
            <label htmlFor="year-day">Day of year</label>
            <input
              aria-label="Day of year"
              id="year-day"
              max={YEAR_DAYS}
              min={0}
              step={1}
              type="range"
              value={clock.value}
              onChange={(event) =>
                clock.setValue(Number(event.currentTarget.value))
              }
            />
            <span className="timeline-value">
              Day {Math.round(clock.value)}
            </span>
          </section>
          <fieldset className="control-group">
            <legend>Orbit guides</legend>
            <label className="checkbox-control">
              <input
                checked={showPerihelion}
                type="checkbox"
                onChange={togglePerihelion}
              />
              Show perihelion
            </label>
          </fieldset>
          <fieldset className="control-group">
            <legend>Stations</legend>
            <ul className="phase-grid">
              {SEASON_STATIONS.map((station) => (
                <li key={station.label}>
                  <button
                    aria-current={station.index === snapshot.seasonIndex}
                    type="button"
                    onClick={() => clock.setValue(station.dayOfYear)}
                  >
                    {station.label}
                  </button>
                </li>
              ))}
            </ul>
          </fieldset>
          <ClassroomTools
            classroomMode={classroomMode}
            predictMode={predictMode}
            onToggleClassroomMode={() =>
              setClassroomMode((current) => !current)
            }
            onTogglePredictMode={togglePredictMode}
          />
        </section>
        <p className="scale-note">
          Educational scale — eccentricity is exaggerated so the Sun’s offset
          from the ellipse centre is visible. Real Earth e ≈ 0.017. Axial tilt
          still decides the seasons.
        </p>
      </section>
    </main>
  );
}
